// Story 11.4: FluidMouseLight — three radial-gradient orbs that follow the
// pointer with separate lerp factors so the slow movement reads as a unified
// glow and fast movement separates into trails. Reuses `loadGsap` and the
// shared `prefersReducedMotion` snapshot from `lib/gsap-setup.ts`.
//
// Layered animations per architecture.md Animation System Integration Rules:
//   - Rule 2: GSAP owns the per-frame lerp via `gsap.ticker.add`.
//   - Rule 4: orbs are CSS-only `<div>`s (radial-gradient + filter:blur),
//     no element receives both Framer and GSAP — the wrapper opacity is
//     tweened by GSAP only.
//
// Inline rgba() literals are used in the gradient strings because CSS custom
// properties cannot be consumed inside `rgba()` without `color-mix()`
// gymnastics. The hex values match `tokens.css` (--color-accent-blue,
// --color-accent-cyan, --color-accent-rose); the rose token stands in for
// "purple" per the Design Decisions log in the Story 11.4 dev notes.

import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { loadGsap, prefersReducedMotion } from '../../../lib/gsap-setup.js';
import { cn } from '../../../lib/cn.js';

export interface FluidMouseLightProps {
  className?: string;
  'data-testid'?: string;
}

const ORB_A_SIZE = 480;
const ORB_B_SIZE = 420;
const ORB_C_SIZE = 380;

const LERP_DEFAULT_A = 0.03;
const LERP_DEFAULT_B = 0.015;
const LERP_DEFAULT_C = 0.008;
const LERP_INTERACTIVE_A = 0.06;
const LERP_INTERACTIVE_B = 0.03;
const LERP_INTERACTIVE_C = 0.016;

// AC5 brightening is applied via `filter: brightness()` rather than `opacity`:
// CSS opacity is spec-clamped to [0, 1], so the AC5 literal tween to 1.3 is a
// visual no-op on every browser. `filter: brightness()` produces the intended
// "30% brighter on interactive hover" effect and composes with the per-orb
// `filter: blur(...)` (parent brightness applies to the blurred composite).
const BRIGHTNESS_INTERACTIVE = 1.3;
const BRIGHTNESS_EDITOR = 0.15;
const BRIGHTNESS_DEFAULT = 1.0;
const TWEEN_DURATION_FAST = 0.22;
const TWEEN_DURATION_RELAXED = 0.3;

type ContextState = 'default' | 'interactive' | 'editor';

const INTERACTIVE_SELECTOR =
  'button, a[href], [role="button"], [role="link"], [data-clickable="true"], [data-fluid-light-interactive="true"]';

export function FluidMouseLight(props: FluidMouseLightProps): JSX.Element | null {
  // Per-mount snapshot of pointer capability (NOT module-scope) so hybrid
  // devices like an iPad with keyboard pick up the orbs after a route
  // transition that re-mounts the AppShell. Module-scope reduced-motion is
  // still authoritative for the reduce branch (Pattern 5). AC4 positive
  // gate on `(pointer: fine)` + AC6 negative gate on `(pointer: coarse)`.
  const shouldRender = useMemo<boolean>(() => {
    if (prefersReducedMotion) return false;
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia !== 'function') return true;
    const isFine = window.matchMedia('(pointer: fine)').matches;
    const isCoarse = window.matchMedia('(pointer: coarse)').matches;
    return isFine && !isCoarse;
  }, []);

  if (!shouldRender) return null;
  return <FluidMouseLightMotion {...props} />;
}

function FluidMouseLightMotion({
  className,
  'data-testid': dataTestId,
}: FluidMouseLightProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const orbARef = useRef<HTMLDivElement | null>(null);
  const orbBRef = useRef<HTMLDivElement | null>(null);
  const orbCRef = useRef<HTMLDivElement | null>(null);
  // Latest pointer target, separate per-orb easing positions, and the
  // current contextual classifier. All refs so the ticker reads them
  // without forcing React re-renders per frame. Lazy-initialized to the
  // viewport center so a pointermove arriving before the mount effect
  // never snaps orbs from (0, 0).
  const initialCenter = (): { x: number; y: number } =>
    typeof window === 'undefined'
      ? { x: 0, y: 0 }
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  const targetRef = useRef<{ x: number; y: number }>(initialCenter());
  const orbAPosRef = useRef<{ x: number; y: number }>(initialCenter());
  const orbBPosRef = useRef<{ x: number; y: number }>(initialCenter());
  const orbCPosRef = useRef<{ x: number; y: number }>(initialCenter());
  const contextRef = useRef<ContextState>('default');
  // Mounted state guard so the ticker only runs after the first client-side
  // commit (keeps SSR snapshots inert).
  const [mounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Pointer tracking — single window listener, passive.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onMove = (e: PointerEvent) => {
      targetRef.current.x = e.clientX;
      targetRef.current.y = e.clientY;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
    };
  }, []);

  // Context detection via pointerover delegation at the window level.
  // pointerover (unlike pointerenter) bubbles, so one listener covers the
  // whole document. Sampled here, NOT on pointermove, to avoid hit-testing
  // every frame.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const setContext = (next: ContextState) => {
      if (contextRef.current === next) return;
      contextRef.current = next;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const brightness =
        next === 'interactive'
          ? BRIGHTNESS_INTERACTIVE
          : next === 'editor'
            ? BRIGHTNESS_EDITOR
            : BRIGHTNESS_DEFAULT;
      const duration = next === 'default' ? TWEEN_DURATION_RELAXED : TWEEN_DURATION_FAST;
      void loadGsap()
        .then((gsap) => {
          gsap.to(wrapper, {
            filter: `brightness(${String(brightness)})`,
            duration,
            ease: 'power2.out',
          });
        })
        .catch(() => undefined);
    };

    const onOver = (e: PointerEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-fluid-light-scope="editor-canvas"]')) {
        setContext('editor');
      } else if (target.closest(INTERACTIVE_SELECTOR)) {
        setContext('interactive');
      } else {
        setContext('default');
      }
    };

    window.addEventListener('pointerover', onOver, { passive: true });
    return () => {
      window.removeEventListener('pointerover', onOver);
    };
  }, []);

  // Ticker-driven lerp — single callback writes transforms to the three orbs.
  useEffect(() => {
    if (!mounted) return;
    let cancelled = false;
    let tickerFn: (() => void) | null = null;

    void loadGsap()
      .then((gsap) => {
        if (cancelled) return;
        tickerFn = () => {
          const ctx = contextRef.current;
          const lerpA = ctx === 'interactive' ? LERP_INTERACTIVE_A : LERP_DEFAULT_A;
          const lerpB = ctx === 'interactive' ? LERP_INTERACTIVE_B : LERP_DEFAULT_B;
          const lerpC = ctx === 'interactive' ? LERP_INTERACTIVE_C : LERP_DEFAULT_C;
          const t = targetRef.current;
          orbAPosRef.current.x += (t.x - orbAPosRef.current.x) * lerpA;
          orbAPosRef.current.y += (t.y - orbAPosRef.current.y) * lerpA;
          orbBPosRef.current.x += (t.x - orbBPosRef.current.x) * lerpB;
          orbBPosRef.current.y += (t.y - orbBPosRef.current.y) * lerpB;
          orbCPosRef.current.x += (t.x - orbCPosRef.current.x) * lerpC;
          orbCPosRef.current.y += (t.y - orbCPosRef.current.y) * lerpC;
          writeTransform(orbARef.current, orbAPosRef.current, ORB_A_SIZE);
          writeTransform(orbBRef.current, orbBPosRef.current, ORB_B_SIZE);
          writeTransform(orbCRef.current, orbCPosRef.current, ORB_C_SIZE);
        };
        gsap.ticker.add(tickerFn);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      const fnToRemove = tickerFn;
      if (fnToRemove) {
        void loadGsap()
          .then((gsap) => gsap.ticker.remove(fnToRemove))
          .catch(() => undefined);
      }
    };
  }, [mounted]);

  return (
    <div
      ref={wrapperRef}
      aria-hidden="true"
      data-testid={dataTestId ?? 'fluid-mouse-light'}
      className={cn('pointer-events-none fixed inset-0 z-[-1] overflow-hidden', className)}
      style={{ filter: 'brightness(1)' }}
    >
      <div
        ref={orbARef}
        className="absolute top-0 left-0 will-change-transform"
        style={{
          width: ORB_A_SIZE,
          height: ORB_A_SIZE,
          background: 'radial-gradient(circle, rgba(37,110,255,0.14) 0%, rgba(37,110,255,0) 60%)',
          filter: 'blur(40px)',
        }}
      />
      <div
        ref={orbBRef}
        className="absolute top-0 left-0 will-change-transform"
        style={{
          width: ORB_B_SIZE,
          height: ORB_B_SIZE,
          background: 'radial-gradient(circle, rgba(45,225,252,0.11) 0%, rgba(45,225,252,0) 60%)',
          filter: 'blur(44px)',
        }}
      />
      <div
        ref={orbCRef}
        className="absolute top-0 left-0 will-change-transform"
        style={{
          width: ORB_C_SIZE,
          height: ORB_C_SIZE,
          background: 'radial-gradient(circle, rgba(219,147,176,0.09) 0%, rgba(219,147,176,0) 60%)',
          filter: 'blur(48px)',
        }}
      />
    </div>
  );
}

function writeTransform(
  el: HTMLDivElement | null,
  pos: { x: number; y: number },
  size: number,
): void {
  if (!el) return;
  el.style.transform = `translate3d(${String(pos.x - size / 2)}px, ${String(
    pos.y - size / 2,
  )}px, 0)`;
}
